import jwt from "jsonwebtoken";
import { JwtPayload } from "./types";
import { logServerError } from "@/services/DatadogService/serverLogger";

/**
 * Verifies a JWT and returns its payload, or null if invalid.
 * SERVER-ONLY: uses jsonwebtoken and crypto polyfills.
 * @param token The JWT token.
 * @param secret The secret to verify the token with.
 * @returns The payload of the token, or null if verification fails.
 */
export const verifyJwt = (token: string, secret: string): JwtPayload | null => {
  try {
    if (!token || !secret) {
      return null;
    }

    return jwt.verify(token, secret) as JwtPayload;
  } catch (error) {
    logServerError(error instanceof Error ? error : new Error(String(error)), {
      service: "JwtService",
      action: "verifyJwt",
    });
    return null;
  }
};

/**
 * Signs a JWT with the given data and secret.
 * SERVER-ONLY: uses jsonwebtoken and crypto polyfills.
 * @param payload The payload to sign.
 * @param secret The secret to sign the payload with.
 * @param expiresIn The expiration time.
 * @returns The signed JWT.
 */
export const signJwt = (
  payload: object,
  secret: string,
  expiresIn: string | number = "15m"
): string => {
  try {
    if (!secret) {
      throw new Error("[JwtService] Error in signJwt: JWT secret is required to sign a token");
    }

    return jwt.sign(payload, secret, { expiresIn } as jwt.SignOptions);
  } catch (error) {
    throw new Error(`[JwtService] Error in signJwt: Failed to sign JWT: ${error}`);
  }
};
